/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Constants } from 'app/core/models/constants';
import { ApiAuthService } from 'app/core/security/api/api-auth.service';
import { BehaviorSubject, catchError, EMPTY, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthResponse } from '../api/auth-response.model';
import { MatDialog } from '@angular/material/dialog';
import { MsalWrapperService } from 'app/modules/msal-wrapper/services/msalwrapper.service';
import { CookieService } from 'app/shared/services/cookie.service';

@Injectable()
export class AuthService {
  // store the URL so we can redirect after logging in
  private redirectUrl = '';
  public loggedInUserId = -1;
  public loggedInUserName = '';
  public loggedInFullName = '';
  public loggedInUserType = -1;

  private refreshTokenInProgress = false;
  // Refresh Token Subject tracks the current token, or is null if no token is currently
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private apiAuth: ApiAuthService, private router: Router, protected dialog: MatDialog, private msalService: MsalWrapperService, private cookieService: CookieService) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (request.url.includes('refreshtoken') || request.url.includes('login')) {
          // We do another check to see if refresh token failed
          // In this case we want to logout user and to redirect it to login page
          if (request.url.includes('refreshtoken')) {
            this.logout();
            this.router.navigateByUrl('/login?r=1');
          }
          // return throwError(error);
        }

        if (error.status !== 401) {
          return throwError(error);
        }

        if (this.refreshTokenInProgress) {
          // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
          // – which means the new token is ready and we can retry the request again
          return this.refreshTokenSubject.pipe(
            filter((result) => result !== null),
            take(1),
            switchMap(() => next.handle(this.addAuthenticationToken(request)))
          );
        } else {
          return this.apiAuth.refreshAccessToken().pipe(
            switchMap((token: any) => {
              console.log('refreshAccessToken returned token');
              console.log(token);
              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(token);
              return next.handle(this.addAuthenticationToken(request));
            }),
            catchError((err: any) => {
              this.refreshTokenInProgress = false;
              // this.snackBar.error('Your session has expired or you are not authorised to complete this action. Please sign in again.');
              if (error.status === 401 || error.status === 400) {
                this.refreshTokenSubject.next(null);
                this.logout();
                this.router.navigateByUrl('/login?r=1');
                return EMPTY;
              } else {
                return throwError(err);
              }
            })
          );
        }
        return throwError(error);
      })
    );
  }
  addAuthenticationToken(request) {
    const accessToken = this.apiAuth.getAccessToken();

    // If access token is null this means that user is not logged in
    // And we return the original request
    if (!accessToken) {
      return request;
    }

    // We clone the request, because the original request is immutable
    return request.clone({
      setHeaders: {
        Authorization: 'Bearer ' + accessToken,
      },
    });
  }

  isLoggedIn(): boolean {
    return this.apiAuth.isAuthenticated();
  }

  login(username: string, password: string, otp: string): Promise<AuthResponse> {
    return this.apiAuth.authenticate(username, password, otp);
  }

  completeOnboardingAndLogin(userId: number, onboardEmailLinkToken: string, otp: string): Promise<AuthResponse> {
    return this.apiAuth.onboardAndAuthenticate(userId, onboardEmailLinkToken, otp);
  }

  logout(): Promise<boolean> {
    if (this.dialog) this.dialog.closeAll();
    localStorage.removeItem(Constants.LocalStorage.KeyAccessToken);
    localStorage.removeItem(Constants.LocalStorage.KeyRefreshToken);
    this.cookieService.delete(Constants.Cookies.KeyLoggedInUserId);
    this.cookieService.delete(Constants.Cookies.KeyLoggedInUserName);
    this.cookieService.delete(Constants.Cookies.KeyLoggedInFullName);
    this.cookieService.delete(Constants.Cookies.KeyLoggedInUserType);
    this.cookieService.delete(Constants.Cookies.KeyOrganiserId);
    return this.apiAuth.deauthenticate();
  }

  async loginViaMicrosoft(username: string): Promise<AuthResponse> {
    await this.msalService.login(username);
    var msAccessToken = await this.msalService.getAccessToken(username);
    return this.apiAuth.authenticateViaMicrosoft(msAccessToken);
  }

  setRedirectUrl(url: string): string {
    this.redirectUrl = url;
    return this.redirectUrl;
  }

  getRedirectUrl(): string {
    return this.redirectUrl.length > 0 ? this.redirectUrl : '/';
  }

  getAccessToken(): string {
    return this.apiAuth.getAccessToken();
  }
}
